import type { RouteRecordRaw } from 'vue-router';
import md from '@/router/middlewares';
import roles from '@/constants/roles';

const vue3Administration: RouteRecordRaw = {
    path: '/administration',
    name: 'administration',
    component: () => import('@/views/administration/AdminMain.vue'),
    redirect: { name: 'administration-management' },
    meta: {
        middlewares: [md.auth, md.accessControl],
    },
    children: [
        // MANAGEMENT
        {
            meta: {
                title: 'administration.management',
                hasChildren: true,
                img: 'ManagementAdmin.svg',
                allowedRoles: [
                    roles.ADMINISTRATOR.value,
                    roles.MANAGER.value,
                    roles.LABORATORY_MANAGER.value,
                    roles.LABORATORY_MICROBIOLOGIST.value,
                ],
            },
            path: 'management',
            name: 'administration-management',
            children: [
                {
                    meta: {
                        title: 'administration.users',
                        allowedRoles: [
                            roles.ADMINISTRATOR.value,
                            roles.MANAGER.value,
                            roles.LABORATORY_MANAGER.value,
                            roles.LABORATORY_MICROBIOLOGIST.value,
                        ],
                    },
                    path: 'users',
                    name: 'administration-management-users',
                    component: () => import('@/views/administration/management/UsersList.vue'),
                },
                {
                    meta: {
                        title: 'administration.users',
                        hidden: true,
                        allowedRoles: [
                            roles.ADMINISTRATOR.value,
                            roles.MANAGER.value,
                            roles.LABORATORY_MANAGER.value,
                            roles.LABORATORY_MICROBIOLOGIST.value,
                        ],
                    },
                    path: 'users/:id/userInfo',
                    name: 'administration-management-users-info',
                    component: () => import('@/views/administration/management/UserInfo.vue'),
                },
                {
                    meta: {
                        title: 'administration.accessGroups',
                        allowedRoles: [
                            roles.ADMINISTRATOR.value,
                            roles.MANAGER.value,
                            roles.LABORATORY_MANAGER.value,
                            roles.LABORATORY_MICROBIOLOGIST.value,
                        ],
                    },
                    path: 'access-groups',
                    name: 'administration-management-accessGroups',
                    component: () => import('@/views/administration/management/AccessGroups.vue'),
                },
                {
                    meta: {
                        title: 'administration.automaticSentences',
                        allowedRoles: [
                            roles.ADMINISTRATOR.value,
                            roles.MANAGER.value,
                            roles.LABORATORY_MANAGER.value,
                            roles.LABORATORY_MICROBIOLOGIST.value,
                        ],
                    },
                    path: 'dynamic-comments',
                    name: 'administration-management-automaticSentences',
                    component: () =>
                        import('@/views/administration/automaticSentences/AutomaticSentences.vue'),
                },
                {
                    meta: {
                        title: 'administration.defaultAnalyses',
                        allowedRoles: [
                            roles.ADMINISTRATOR.value,
                            roles.MANAGER.value,
                            roles.LABORATORY_MANAGER.value,
                            roles.LABORATORY_MICROBIOLOGIST.value,
                        ],
                    },
                    path: 'default-cards',
                    name: 'administration-management-defaultAnalyses',
                    component: () =>
                        import(
                            '@/views/administration/management/default-analyses/AdminDefaultAnalysesPage.vue'
                        ),
                },
                {
                    meta: {
                        title: 'administration.standardFamilies',
                        allowedRoles: [
                            roles.ADMINISTRATOR.value,
                            roles.MANAGER.value,
                            roles.LABORATORY_MANAGER.value,
                            roles.LABORATORY_MICROBIOLOGIST.value,
                        ],
                    },
                    path: 'standard-&-limits',
                    name: 'administration-management-standardFamilies',
                    component: () =>
                        import(
                            '@/views/administration/management/templates-norms/AdminStandardFamilies.vue'
                        ),
                },
                // {
                //     meta: {
                //         title: 'administration.contracts',
                //         allowedRoles: [
                //             roles.ADMINISTRATOR.value,
                //             roles.MANAGER.value,
                //             roles.LABORATORY_MANAGER.value,
                //             roles.LABORATORY_MICROBIOLOGIST.value,
                //         ],
                //     },
                //     path: 'contracts',
                //     name: 'administration-management-contracts',
                //     component: () => import('@/views/administration/management/Contracts.vue'),
                // },
                {
                    meta: {
                        title: 'administration.addOns',
                        allowedRoles: [
                            roles.ADMINISTRATOR.value,
                            roles.MANAGER.value,
                            roles.LABORATORY_MANAGER.value,
                            roles.LABORATORY_MICROBIOLOGIST.value,
                        ],
                    },
                    path: 'add-ons',
                    name: 'administration-management-features',
                    component: () => import('@/views/administration/management/MFeatures.vue'),
                },
                {
                    meta: {
                        title: 'administration.billing',
                        allowedRoles: [roles.ADMINISTRATOR.value, roles.MANAGER.value],
                    },
                    path: 'billing',
                    name: 'administration-management-billingRegister',
                    component: () =>
                        import(
                            '@/views/administration/aquassistBilling/billingRegisterComponent/CurrentBillingSummary.vue'
                        ),
                },
            ],
        },
        // CONFIGURATION
        {
            meta: {
                title: 'administration.configuration',
                hasChildren: true,
                img: 'AdminConfig.svg',
                allowedRoles: [
                    roles.ADMINISTRATOR.value,
                    roles.MANAGER.value,
                    roles.LABORATORY_MANAGER.value,
                    roles.LABORATORY_MICROBIOLOGIST.value,
                ],
            },
            path: 'configuration',
            name: 'administration-configuration',
            children: [
                {
                    meta: {
                        title: 'administration.analyses',
                        hasChildren: true,
                        allowedRoles: [
                            roles.ADMINISTRATOR.value,
                            roles.MANAGER.value,
                            roles.LABORATORY_MANAGER.value,
                            roles.LABORATORY_MICROBIOLOGIST.value,
                        ],
                    },
                    path: 'analyses',
                    name: 'administration-configuration-analyses',
                    children: [
                        {
                            meta: {
                                title: 'administration.analysesCategories',
                                allowedRoles: [
                                    roles.ADMINISTRATOR.value,
                                    roles.MANAGER.value,
                                    roles.LABORATORY_MANAGER.value,
                                    roles.LABORATORY_MICROBIOLOGIST.value,
                                ],
                            },
                            path: 'categories',
                            name: 'administration-configuration-analyses-categories',
                            component: () =>
                                import(
                                    '@/views/administration/configuration/analyses/AnalysisCategories.vue'
                                ),
                        },
                        {
                            meta: {
                                title: 'administration.analysesTypes',
                                allowedRoles: [
                                    roles.ADMINISTRATOR.value,
                                    roles.MANAGER.value,
                                    roles.LABORATORY_MANAGER.value,
                                    roles.LABORATORY_MICROBIOLOGIST.value,
                                ],
                            },
                            path: 'types',
                            name: 'administration-configuration-analyses-types',
                            component: () =>
                                import(
                                    '@/views/administration/configuration/analyses/AnalysisTypes.vue'
                                ),
                        },
                    ],
                },
                {
                    meta: {
                        title: 'administration.coupons',
                        allowedRoles: [
                            roles.ADMINISTRATOR.value,
                            roles.MANAGER.value,
                            roles.LABORATORY_MANAGER.value,
                            roles.LABORATORY_MICROBIOLOGIST.value,
                        ],
                    },
                    path: 'coupons',
                    name: 'administration-configuration-coupons',
                    component: () => import('@/views/NotImplementedYet.vue'),
                },
                {
                    meta: {
                        title: 'administration.maintenanceSchedule',
                        allowedRoles: [
                            roles.ADMINISTRATOR.value,
                            roles.MANAGER.value,
                            roles.LABORATORY_MANAGER.value,
                            roles.LABORATORY_MICROBIOLOGIST.value,
                        ],
                    },
                    path: 'maintenance-schedule',
                    name: 'administration-configuration-maintenanceSchedule',
                    component: () => import('@/views/NotImplementedYet.vue'),
                },
                {
                    meta: {
                        title: 'administration.procedures',
                        allowedRoles: [
                            roles.ADMINISTRATOR.value,
                            roles.MANAGER.value,
                            roles.LABORATORY_MANAGER.value,
                            roles.LABORATORY_MICROBIOLOGIST.value,
                        ],
                    },
                    path: 'procedures',
                    name: 'administration-configuration-procedures',
                    component: () => import('@/views/NotImplementedYet.vue'),
                },
                {
                    meta: {
                        title: 'administration.products',
                        allowedRoles: [
                            roles.ADMINISTRATOR.value,
                            roles.MANAGER.value,
                            roles.LABORATORY_MANAGER.value,
                            roles.LABORATORY_MICROBIOLOGIST.value,
                        ],
                    },
                    path: 'products',
                    name: 'administration-configuration-products',
                    component: () => import('@/views/NotImplementedYet.vue'),
                },
                {
                    meta: {
                        title: 'administration.erp',
                        allowedRoles: [
                            roles.ADMINISTRATOR.value,
                            roles.MANAGER.value,
                            roles.LABORATORY_MANAGER.value,
                            roles.LABORATORY_MICROBIOLOGIST.value,
                        ],
                    },
                    path: 'erp',
                    name: 'administration-configuration-erp',
                    component: () => import('@/views/NotImplementedYet.vue'),
                },
                {
                    meta: {
                        title: 'administration.companyInformation',
                        allowedRoles: [roles.ADMINISTRATOR.value, roles.MANAGER.value],
                    },
                    path: 'service-provider',
                    name: 'administration-configuration-serviceProvider',
                    component: () =>
                        import('@/views/administration/configuration/ServiceProvider.vue'),
                },
            ],
        },
        // ADVANCE CONFIGURATION
        {
            meta: {
                title: 'administration.advancedConfiguration',
                hasChildren: true,
                prependIcon: 'mdi-cogs',
                allowedRoles: [roles.ADMINISTRATOR.value, roles.MANAGER.value],
            },
            path: 'advance-configuration',
            name: 'administration-advance-configuration',
            children: [
                {
                    meta: {
                        title: 'administration.serviceProviders',
                        allowedRoles: [roles.ADMINISTRATOR.value, roles.MANAGER.value],
                    },
                    path: 'service-providers',
                    name: 'administration-advance-configuration-service-providers',
                    component: () =>
                        import('@/views/administration/advancedConfiguration/ServiceProviders.vue'),
                },
                {
                    meta: {
                        title: 'administration.nodes',
                        allowedRoles: [roles.ADMINISTRATOR.value, roles.MANAGER.value],
                    },
                    path: 'nodes',
                    name: 'administration-advance-configuration-nodes',
                    component: () => import('@/views/NotImplementedYet.vue'),
                },
                {
                    meta: {
                        title: 'administration.videoCategories',
                        allowedRoles: [roles.ADMINISTRATOR.value, roles.MANAGER.value],
                    },
                    path: 'video-categories',
                    name: 'administration-advance-configuration-video-categories',
                    component: () => import('@/views/NotImplementedYet.vue'),
                },
                {
                    meta: {
                        title: 'administration.devices',
                        allowedRoles: [roles.ADMINISTRATOR.value, roles.MANAGER.value],
                    },
                    path: 'devices',
                    name: 'administration-advance-configuration-devices',
                    component: () => import('@/views/NotImplementedYet.vue'),
                },
                {
                    meta: {
                        title: 'administration.deviceAnalysisKeys',
                        allowedRoles: [roles.ADMINISTRATOR.value, roles.MANAGER.value],
                    },
                    path: 'deviceAnalysisKeys',
                    name: 'administration-advance-configuration-deviceAnalysisKeys',
                    component: () => import('@/views/NotImplementedYet.vue'),
                },
                {
                    meta: {
                        title: 'reports.types.pageName',
                        allowedRoles: [roles.ADMINISTRATOR.value, roles.MANAGER.value],
                    },
                    path: 'report-types',
                    name: 'administration-advance-configuration-report-types',
                    component: () => import('@/views/NotImplementedYet.vue'),
                },
                {
                    meta: {
                        title: 'administration.analysisTypeTypes',
                        allowedRoles: [roles.ADMINISTRATOR.value, roles.MANAGER.value],
                    },
                    path: 'analysis-type-types',
                    name: 'administration-advance-configuration-analysis-type-types',
                    component: () => import('@/views/NotImplementedYet.vue'),
                },
                {
                    meta: {
                        title: 'administration.workOrderTypes',
                        allowedRoles: [roles.ADMINISTRATOR.value, roles.MANAGER.value],
                    },
                    path: 'work-order-types',
                    name: 'administration-advance-configuration-work-order-types',
                    component: () => import('@/views/NotImplementedYet.vue'),
                },
                {
                    meta: {
                        title: 'administration.features',
                        allowedRoles: [roles.ADMINISTRATOR.value, roles.MANAGER.value],
                    },
                    path: 'features',
                    name: 'administration-advance-configuration-features',
                    component: () =>
                        import('@/views/administration/advancedConfiguration/Features.vue'),
                },
                {
                    meta: {
                        title: 'administration.release',
                        allowedRoles: [roles.ADMINISTRATOR.value, roles.MANAGER.value],
                    },
                    path: 'release',
                    name: 'administration-advance-configuration-release',
                    component: () => import('@/views/NotImplementedYet.vue'),
                },
                {
                    meta: {
                        title: 'privacyPolicy.title',
                        allowedRoles: [roles.ADMINISTRATOR.value, roles.MANAGER.value],
                    },
                    path: 'privacy-policy',
                    name: 'administration-advance-configuration-privacy-policy',
                    component: () =>
                        import('@/views/administration/advancedConfiguration/PrivacyPolicy.vue'),
                },
                {
                    meta: {
                        title: 'administration.serviceProviderBilling',
                        allowedRoles: [roles.ADMINISTRATOR.value, roles.MANAGER.value],
                    },
                    path: 'service-provider-billing',
                    name: 'administration-advance-configuration-service-provider-billing',
                    component: () => import('@/views/NotImplementedYet.vue'),
                },
                {
                    meta: {
                        title: 'administration.logs',
                        allowedRoles: [roles.ADMINISTRATOR.value, roles.MANAGER.value],
                    },
                    path: 'logs',
                    name: 'administration-advance-configuration-logs',
                    component: () => import('@/views/NotImplementedYet.vue'),
                },
                {
                    meta: {
                        title: 'administration.analysisParamsUpload',
                        allowedRoles: [roles.ADMINISTRATOR.value, roles.MANAGER.value],
                    },
                    path: 'analysis-params-upload',
                    name: 'administration-advance-configuration-analysisParamsUpload',
                    component: () => import('@/views/NotImplementedYet.vue'),
                },
                {
                    meta: {
                        title: 'administration.aiConfig',
                        allowedRoles: [roles.ADMINISTRATOR.value],
                    },
                    path: 'ai-configuration',
                    name: 'administration-advance-configuration-ai-configuration',
                    component: () =>
                        import('@/views/administration/advancedConfiguration/ConfigurationAI.vue'),
                },
                {
                    meta: {
                        title: 'administration.contactFunctions',
                        allowedRoles: [roles.ADMINISTRATOR.value, roles.MANAGER.value],
                    },
                    path: 'contact-functions',
                    name: 'administration-advance-configuration-contact-functions',
                    component: () =>
                        import('@/views/administration/advancedConfiguration/ContactFunctions.vue'),
                },
            ],
        },
        // AQ Billing
        {
            meta: {
                title: 'administration.aquassistBilling',
                hasChildren: true,
                img: 'AquassistBilling.svg',
                allowedRoles: [
                    roles.ADMINISTRATOR.value,
                    roles.MANAGER.value,
                    roles.FINANCIAL_ADMINISTRATOR.value,
                ],
            },
            path: 'aquassist-billing',
            name: 'administration-aquassistBilling',
            children: [
                {
                    meta: {
                        title: 'administration.pricingManagement',
                        allowedRoles: [roles.ADMINISTRATOR.value, roles.MANAGER.value],
                    },
                    path: 'pricing-management',
                    name: 'administration-configuration-pricingManagement',
                    component: () => import('@/views/NotImplementedYet.vue'),
                },
                {
                    meta: {
                        title: 'administration.billingRegister',
                        allowedRoles: [
                            roles.ADMINISTRATOR.value,
                            roles.MANAGER.value,
                            roles.FINANCIAL_ADMINISTRATOR.value,
                        ],
                    },
                    path: 'billing-register',
                    name: 'administration-configuration-billingRegister',
                    component: () =>
                        import('@/views/administration/aquassistBilling/BillingRegister.vue'),
                },
            ],
        },
    ],
};

const vue2Administration: RouteRecordRaw = {
    path: '/administration/:pathMatch(.*)*',
    name: 'administration',
    component: { render: () => null },
    beforeEnter: (to) => {
        window.location.replace(`${process.env.VUE_APP_BASE_URL}${to.fullPath}`);
        return false;
    },
};
export const isVue2Administration = (): boolean =>
    process.env.VUE_APP_ENV_LABEL?.toLowerCase() === 'production';

const administration = isVue2Administration() ? vue2Administration : vue3Administration;
export default administration;
